import React from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Grid, Typography } from '@mui/material';
import { Form, Formik, FormikHelpers } from 'formik';
import * as yup from 'yup';
import { usePostHog } from 'posthog-js/react';
import { KeyedMutator } from 'swr/_internal';
import { AccountData, AccountService, PassOrderPageData, PassType } from '../../../api';
import { UseAuthPermissions } from '../../../session/UseAuthPermissions';
import { useAuth } from '../../../session/InternalAuthProvider';
import { FormTextField } from '../../../experienceSettings/formComponents/FormTextField';
import { getCurrencySymbol } from '../../../utils/utils';
import { SectionSaveRegistrar } from '../../../experienceSettings/saveHeader/SectionSaveRegistrar';
import type { ChangeEntry } from '../../../experienceSettings/saveHeader/SaveHeaderContext';
import { PassSettingsPageTabs } from '../PassSettingsPageTabs';
import { isFixedScheduleMonthly } from '../../management/utils';
import { FixedSchedulePricingSection } from './FixedSchedulePricingSection';

type PassPricingSectionProps = {
  passOrderPage: PassOrderPageData;
  updateLocalAccount: KeyedMutator<AccountData>;
};

type PassPricingFormValues = {
  price: number;
  num_punches: number | undefined;
  validity_days: number;
  guest_pass_quota: number | undefined;
};

export const PassPricingSection = ({ passOrderPage, updateLocalAccount }: PassPricingSectionProps) => {
  if (isFixedScheduleMonthly(passOrderPage.pass_type)) {
    return <FixedSchedulePricingSection passOrderPage={passOrderPage} updateLocalAccount={updateLocalAccount} />;
  }
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  return <StandardPassPricingSection passOrderPage={passOrderPage} updateLocalAccount={updateLocalAccount} />;
};

function StandardPassPricingSection({ passOrderPage, updateLocalAccount }: PassPricingSectionProps) {
  const { t } = useTranslation();
  const { readOnly } = UseAuthPermissions();
  const { authState } = useAuth();
  const currencySymbol = getCurrencySymbol(authState.account as unknown as AccountData);
  const posthog = usePostHog();

  const validationSchema = yup.object().shape({
    price: yup.number().positive(t('editOrderPage.yup.hasToBePoitive')).required(t('editOrderPage.yup.required')),
    num_punches: yup
      .number()
      .transform((val, orig) => (orig === '' || orig === null ? undefined : val))
      .positive(t('editOrderPage.yup.hasToBePoitive'))
      .integer()
      .optional(),
    validity_days: yup
      .number()
      .positive(t('editOrderPage.yup.hasToBePoitive'))
      .integer()
      .required(t('editOrderPage.yup.required')),
    guest_pass_quota: yup
      .number()
      .transform((val, orig) => (orig === '' || orig === null ? undefined : val))
      .positive(t('editOrderPage.yup.hasToBePoitive'))
      .integer()
      .optional(),
  });

  const initialValues: PassPricingFormValues = {
    price: passOrderPage.price,
    num_punches: passOrderPage.num_punches,
    validity_days: passOrderPage.validity_days,
    guest_pass_quota: passOrderPage.guest_pass_quota,
  };

  const handleSubmit = async (values: PassPricingFormValues, { setStatus }: FormikHelpers<PassPricingFormValues>) => {
    try {
      posthog.capture('Editing pass settings', { section: 'pricingSection', passOrderPageId: passOrderPage.id });
      const wasEnabled = !!passOrderPage.guest_pass_quota && passOrderPage.guest_pass_quota > 0;
      const isEnabled = !!values.guest_pass_quota && values.guest_pass_quota > 0;
      const shouldBackfillAllowGuest = !wasEnabled && isEnabled;
      const redeemable_on = shouldBackfillAllowGuest
        ? (passOrderPage.redeemable_on || []).map((item) => ({ ...item, allow_guest: true }))
        : undefined;
      const updatedAccount = await AccountService.updatePassOrderPage({
        id: passOrderPage.id,
        price: values.price,
        num_punches: values.num_punches || undefined,
        validity_days: values.validity_days,
        guest_pass_quota: values.guest_pass_quota || undefined,
        ...(redeemable_on ? { redeemable_on } : {}),
      });
      await updateLocalAccount(updatedAccount);
    } catch (e) {
      setStatus({ error: (e as Error)?.message || t('saveHeader.errorGeneric') });
      throw e;
    }
  };

  const getChanges = (values: PassPricingFormValues, iv: PassPricingFormValues): ChangeEntry[] => {
    const changes: ChangeEntry[] = [];
    const compare = (key: keyof PassPricingFormValues, label: string, suffix = '') => {
      if (values[key] !== iv[key]) {
        changes.push({
          label,
          from: iv[key] != null ? `${iv[key]}${suffix}` : '—',
          to: values[key] != null ? `${values[key]}${suffix}` : '—',
        });
      }
    };
    compare('price', t('passes.price'), ` ${currencySymbol}`);
    compare('num_punches', t('passes.num_punches'));
    compare('validity_days', t('passes.validity_days'));
    compare(
      'guest_pass_quota',
      passOrderPage.pass_type === PassType.UNLIMITED_MONTHLY
        ? t('passes.guest_pass_quota_membership')
        : t('passes.guest_pass_quota_punch'),
    );
    return changes;
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}>
      {(formik) => {
        const { isSubmitting } = formik;
        const formIsDisabled = readOnly || isSubmitting;
        return (
          <Form onSubmit={formik.handleSubmit}>
            <SectionSaveRegistrar tabKey={PassSettingsPageTabs.PRICING} getChanges={getChanges} disabled={readOnly} />
            <Grid container justifyContent="space-between" flexWrap="nowrap" alignItems="flex-start">
              <Grid container flexDirection="column" gap={1}>
                <FormTextField
                  type="number"
                  width={200}
                  fieldName="price"
                  label={t('passes.price')}
                  endAdornment={currencySymbol}
                  disabled={formIsDisabled}
                  isFieldUpdate
                />
                <FormTextField
                  type="number"
                  width={200}
                  fieldName="num_punches"
                  label={t('passes.num_punches')}
                  disabled={formIsDisabled}
                  isFieldUpdate
                />
                <FormTextField
                  type="number"
                  width={200}
                  fieldName="validity_days"
                  label={t('passes.validity_days')}
                  disabled={formIsDisabled}
                  isFieldUpdate
                />

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6">{t('passes.guest_visits_section_title')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>
                  {passOrderPage.pass_type === PassType.UNLIMITED_MONTHLY
                    ? t('passes.guest_visits_section_description_membership')
                    : t('passes.guest_visits_section_description_punch')}
                </Typography>
                <FormTextField
                  type="number"
                  width={200}
                  fieldName="guest_pass_quota"
                  label={
                    passOrderPage.pass_type === PassType.UNLIMITED_MONTHLY
                      ? t('passes.guest_pass_quota_membership')
                      : t('passes.guest_pass_quota_punch')
                  }
                  disabled={formIsDisabled}
                  isFieldUpdate
                />
                <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 520 }}>
                  {t('passes.guest_visits_section_hint')}
                </Typography>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
}
